import { useState } from 'react';
import axios from 'axios';
import { MailWarning, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// In-app email confirmation for new owners (they can use the app straight away).
export const EmailVerifyBanner = () => {
  const { user, verifyEmail } = useAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(null);
  const [sendFailed, setSendFailed] = useState(false);

  if (!user || user.role !== 'main_user' || user.email_verified !== false) return null;

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) return toast.error('Enter the 6-digit code from the email');
    setBusy('verify');
    try {
      await verifyEmail(user.email, code.trim());
      toast.success('Email confirmed — thank you!');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Invalid or expired code');
    } finally {
      setBusy(null);
    }
  };

  const handleResend = async () => {
    setBusy('resend');
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/me/resend-verification`);
      setSendFailed(!data.sent);
      if (data.sent) toast.success(`New code sent to ${user.email} — check spam/junk too`);
      else toast.error(data.error || "We couldn't send the email");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : "We couldn't send the email");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-amber-50 border-b-2 border-amber-200 px-4 lg:px-8 py-3" data-testid="email-verify-banner">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex items-start gap-2 flex-1 text-sm text-amber-900">
          <MailWarning className="w-5 h-5 shrink-0 mt-0.5" />
          <p>
            <strong>Confirm your email.</strong> Enter the 6-digit code we sent to <strong>{user.email}</strong>.
            {' '}Not there? Check your <strong>spam / junk</strong> folder, or send a new code.
            {sendFailed && <span className="block text-red-700 font-medium" data-testid="email-verify-send-failed">We couldn't send the email just now — please try again in a minute or contact support@fleetcost.app.</span>}
          </p>
        </div>
        <form onSubmit={handleVerify} className="flex items-center gap-2">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
            inputMode="numeric"
            className="w-28 font-data tracking-widest bg-white"
            data-testid="email-verify-code-input"
          />
          <Button type="submit" size="sm" className="bg-gray-800 hover:bg-gray-700" disabled={!!busy} data-testid="email-verify-submit-btn">
            {busy === 'verify' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={handleResend} disabled={!!busy} data-testid="email-verify-resend-btn">
            {busy === 'resend' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send new code'}
          </Button>
        </form>
      </div>
    </div>
  );
};
